import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { ComponentTemplate } from "@v1/types/component-template";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { DebouncedInput } from "@v1/components/common/designSystem/Input";
import ComponentTemplateDragOverlay from "@v1/components/ComponentTemplateDragOverlay";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { DeprecatedEmptyState } from "@v1/components/designSystem/empty-state/EmptyState";
import { Menu } from "@v1/components/designSystem/menu/Menu";
import Popover from "@v1/components/designSystem/popover/Popover";
import IsolatedReploComponent from "@v1/components/IsolatedReploComponent";
import useCurrentDragType from "@v1/hooks/useCurrentDragType";
import useInfiniteTemplates from "@v1/hooks/useInfiniteTemplates";
import useSearchableTemplateLeftBarCategories from "@v1/hooks/useSearchableTemplateLeftBarCategories";
import { getTemplatePreviewImageSrc } from "@v1/utils/component-template";
import { useIntervalOptions } from "@v1/utils/use-interval-options";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useModal } from "@v2/store/modalStore";
import { ImageOff, MoreVertical, Search, Trash } from "lucide-react";
import { getTemplateName } from "replo-templates";
import { useOnScreen } from "replo-utils/react/use-on-screen";

const ComponentPreview = ({
  componentTemplate,
  imageSrc,
  isSaved,
}: {
  componentTemplate: ComponentTemplate;
  imageSrc?: string;
  isSaved: boolean;
}) => {
  const [imageError, setImageError] = React.useState(false);

  if (isFeatureEnabled("component-previews") && componentTemplate.template) {
    return (
      <IsolatedReploComponent
        component={componentTemplate.template}
        variant="desktop"
      />
    );
  }

  if (imageError || !imageSrc) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <ImageOff size={24} className="text-muted mb-2" />
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={getTemplateName(componentTemplate) as string}
      className={twMerge(
        isSaved
          ? "absolute inset-0 m-auto h-full w-full object-contain object-center"
          : "absolute top-0 w-full object-cover",
      )}
      style={{ pointerEvents: "none" }}
      onError={() => setImageError(true)}
    />
  );
};

const ComponentTemplateListItem: React.FC<{
  componentTemplate: ComponentTemplate;
}> = ({ componentTemplate }) => {
  const modal = useModal();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = React.useState(false);
  const { currentDragType } = useCurrentDragType();
  const isSaved = componentTemplate.scope === "store";

  const menuItems: MenuItem[] = [
    {
      id: "deleteTemplate",
      type: "leaf" as const,
      title: "Delete",
      variant: "default" as const,
      size: "sm" as const,
      startEnhancer: <Trash size={12} />,
      onSelect: () => {
        modal.openModal({
          type: "deleteComponentTemplateConfirmationModal",
          props: {
            template: componentTemplate,
          },
        });
      },
    },
  ];
  const templateName = getTemplateName(componentTemplate) as string;
  const imageSrc = getTemplatePreviewImageSrc(componentTemplate) ?? undefined;

  return (
    <Popover.Root
      isOpen={isPopoverOpen}
      onOpenChange={(open) => {
        setIsPopoverOpen(open);
        if (!open) {
          setIsPreviewLoaded(false);
        }
      }}
    >
      <Popover.Anchor
        asChild
        onMouseEnter={() => {
          if (!isMenuOpen && !currentDragType && isSaved) {
            setIsPopoverOpen(true);
          }
        }}
        onMouseLeave={() => {
          setIsPopoverOpen(false);
        }}
      >
        <li key={componentTemplate.id}>
          <div
            className={twMerge(
              "border-0.5 border-border bg-neutral-soft group relative flex aspect-[5/4] w-full cursor-pointer flex-col justify-center gap-1 rounded-lg p-1.5",
              isSaved && "hover:bg-white",
              isMenuOpen && "bg-white",
            )}
            data-testid="component-template-draggable"
          >
            {componentTemplate.template && (
              <ComponentTemplateDragOverlay
                dragType="newComponent"
                template={componentTemplate}
                identifier={componentTemplate.id}
                scope="left-bar"
                showOutlineOnHover={false}
              />
            )}
            {isSaved && (
              <div className="relative flex flex-shrink-0 items-start justify-between">
                <Tag size="sm" variant="info">
                  Saved
                </Tag>
                <div
                  className={twMerge(
                    "invisible relative group-hover:visible",
                    isMenuOpen && "visible",
                  )}
                >
                  <Menu
                    isOpen={isMenuOpen}
                    onRequestOpen={() => setIsMenuOpen(true)}
                    onRequestClose={() => setIsMenuOpen(false)}
                    align="start"
                    size="sm"
                    side="right"
                    disableTriggerFocusOnClose
                    items={menuItems}
                    contentClassNames="w-40"
                    trigger={
                      <DeprecatedIconButton
                        variant="tertiary"
                        size="sm"
                        icon={<MoreVertical size={12} />}
                        UNSAFE_className="absolute top-0 right-0 z-[2147483649]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMenuOpen(true);
                        }}
                      />
                    }
                  />
                </div>
              </div>
            )}
            <div className="relative flex-grow overflow-hidden">
              <ComponentPreview
                componentTemplate={componentTemplate}
                imageSrc={imageSrc}
                isSaved={isSaved}
              />
            </div>

            <div className="text-muted flex-shrink-0 truncate text-center text-2xs">
              {templateName}
            </div>
          </div>
        </li>
      </Popover.Anchor>
      <Popover.Content
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        shouldPreventDefaultOnInteractOutside={false}
        hideCloseButton
        side="right"
        align="start"
        sideOffset={12}
        className={twMerge(
          "pointer-events-none isolation-auto flex w-[250px] flex-col gap-1 rounded-md opacity-0",
          isPreviewLoaded && "opacity-100",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-semibold w-full break-all">
            {templateName}
          </span>
          {isSaved && (
            <Tag size="sm" variant="info">
              Saved
            </Tag>
          )}
        </div>
        <img
          src={imageSrc}
          alt={templateName}
          className="max-h-[300px] rounded object-contain"
          onLoad={() => setIsPreviewLoaded(true)}
        />
      </Popover.Content>
    </Popover.Root>
  );
};

const ComponentTemplatesList = ({
  searchString,
}: {
  searchString?: string;
}) => {
  const { project } = useCurrentProjectContext();
  const {
    componentTemplatesList: savedComponentTemplates,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteTemplates({
    searchText: searchString,
    includeComponent: true,
    selectedCategories: [],
    scope: "store",
    componentCategoryType: ["component"],
    pageSize: 20,
    storeId: project?.id,
    selectedIndustries: [],
    includeArchivedTemplates: false,
  });

  const leftBarCategories = useSearchableTemplateLeftBarCategories(
    "left-bar",
    searchString ?? "",
  );
  const baseComponentTemplates = leftBarCategories.categories.flatMap(
    (category) => {
      return category.subCategories.flatMap(
        (subCategory) => subCategory.componentTemplates,
      );
    },
  );

  // Separate saved templates (paginated) from base templates (static)
  const savedTemplatesForDisplay = React.useMemo(() => {
    if (!savedComponentTemplates?.length) {
      return [];
    }
    return savedComponentTemplates;
  }, [savedComponentTemplates]);

  // Note (Patrick, 2024-12-16): Use useOnScreen hook to trigger fetch when
  // the load more element comes into view. This handles the case where the first
  // page doesn't produce overflow on tall screens.
  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const loadMoreRef = useOnScreen(handleLoadMore, {
    rootMargin: "100px",
    continuous: true,
  });

  const hasAnyTemplates =
    (savedComponentTemplates?.length ?? 0) + baseComponentTemplates.length > 0;

  if (isLoading) {
    return (
      <ul className="h-full">
        <div className="grid grid-cols-2 gap-1.5 pb-3">
          {new Array(6).fill(0).map((_, itemIndex) => (
            <li key={itemIndex}>
              <div className="relative aspect-[16/9] w-full">
                <Skeleton className="border-border h-full w-full rounded-lg border-[0.5px]" />
              </div>
            </li>
          ))}
        </div>
      </ul>
    );
  }

  if (!hasAnyTemplates) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <DeprecatedEmptyState
          title="No Results"
          description={
            searchString
              ? `No results matching "${searchString}"`
              : "No results found"
          }
          icon={<Search className="icon-size-default" />}
          size="sm"
          UNSAFE_className="py-[15%]"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3">
      <ul>
        {/* Saved templates (paginated) */}
        <div className="grid grid-cols-2 gap-1.5">
          {savedTemplatesForDisplay.map((componentTemplate) => (
            <ComponentTemplateListItem
              key={componentTemplate.id}
              componentTemplate={componentTemplate}
            />
          ))}
        </div>
        {/* Invisible trigger for IntersectionObserver */}
        <div ref={loadMoreRef} className="h-1.5" />
        {/* Loading skeleton when fetching more */}
        {hasNextPage && (
          <div className="grid grid-cols-2 gap-1.5 pb-1.5">
            {new Array(4).fill(0).map((_, itemIndex) => (
              <li key={`loader-${itemIndex}`}>
                <div className="relative aspect-[16/9] w-full">
                  <Skeleton className="border-border h-full w-full rounded-lg border-[0.5px]" />
                </div>
              </li>
            ))}
          </div>
        )}
        {/* Base templates (static) */}
        <div className="grid grid-cols-2 gap-1.5 pb-3">
          {baseComponentTemplates.map((componentTemplate) => (
            <ComponentTemplateListItem
              key={componentTemplate.id}
              componentTemplate={componentTemplate}
            />
          ))}
        </div>
      </ul>
    </div>
  );
};

const ComponentsPane: React.FC = () => {
  const [searchString, setSearchString] = React.useState("");
  const placeholder = useIntervalOptions({
    options: [
      "Buy box with carousel...",
      "Product grid with images...",
      "Image carousel...",
      "Buy now button...",
      "Accordion component...",
    ],
    interval: 5000,
  });
  const logEvent = useLogAnalytics();

  return (
    <div className="flex h-full w-full flex-col items-center gap-3 p-3 pt-0">
      <div className="sticky top-0 w-full bg-white">
        <DebouncedInput
          placeholder={placeholder}
          value={searchString}
          onValueChange={setSearchString}
          name="search-input"
          onBlur={() => {
            if (searchString.length > 0) {
              logEvent("components.search", {
                searchString: searchString,
              });
            }
          }}
          autoComplete="off"
          startEnhancer={<Search className="h-3 w-3" />}
          autoFocus={true}
        />
      </div>

      <Separator />

      <div className="min-h-0 w-full flex-1 px-3">
        <div className="no-scrollbar h-full overflow-y-auto">
          <div
            className="no-scrollbar absolute inset-0 mt-[92px] flex flex-col overflow-y-scroll bg-transparent p-3 pt-0"
            id="components-pane-search-wrapper"
          >
            <ComponentTemplatesList searchString={searchString} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentsPane;
